import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { ProductCard, Product } from '@/components/product/ProductCard';
import { demoByCategory } from '@/data/demoProducts';
import { matchesSubcategory, matchesColour, productColour } from '@/lib/catalog';
import { getHistory } from '@/lib/tracking';
import { galleryForGarment, seedFromId } from '@/data/catalogImages';
import { Seo } from '@/components/Seo';

/** Demo catalogue only renders when explicitly enabled (off in production). */
const SHOW_DEMO = import.meta.env.VITE_SHOW_DEMO === 'true';

/* ────────────── Static category meta ────────────── */


const headerMeta: Record<string, { title: string; intro: string; img: string; count: string }> = {
  all:         { title: 'All listings', intro: 'Everything verified, payment-protected, and ready to ship via locker.', img: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1920&h=600&fit=crop&auto=format&q=80', count: '4,284' },
  womenswear:  { title: 'Womenswear',  intro: 'From cashmere coats to cult-favourite jeans - hand-picked editorial pieces, verified at the door.', img: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=1920&h=600&fit=crop&auto=format&q=80', count: '1,284' },
  menswear:    { title: 'Menswear',    intro: 'Heritage tailoring and modern essentials. Carefully verified, locker-shipped.',                       img: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=1920&h=600&fit=crop&auto=format&q=80', count: '932' },
  shoes:       { title: 'Shoes',       intro: 'Loafers, trainers, boots - only the cleanest soles make it through verification.',                     img: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=1920&h=600&fit=crop&auto=format&q=80', count: '617' },
  bags:        { title: 'Bags',        intro: 'Authenticated leather, holdalls and totes. Every serial cross-checked.',                                img: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=1920&h=600&fit=crop&auto=format&q=80', count: '481' },
  electronics: { title: 'Electronics', intro: 'Phones, laptops, headphones - IMEI and serials screened before listing.',                         img: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1920&h=600&fit=crop&auto=format&q=80', count: '305' },
  watches:     { title: 'Watches',     intro: 'Heirloom and modern - physically inspected at our hub for anything over £150.',                         img: 'https://images.unsplash.com/photo-1524592094714-0f0654e20314?w=1920&h=600&fit=crop&auto=format&q=80', count: '124' },
  kids:        { title: 'Kids',               intro: 'Gentle-cycle classics. Outgrown, never outloved.',                                                           img: 'https://images.unsplash.com/photo-1503944168849-8bf86cb31b6c?w=1920&h=600&fit=crop&auto=format&q=80', count: '203' },
  traditional: { title: 'Traditional Attire', intro: 'Authentic heritage fashion - Ankara prints, Adire tie-dye, aso-oke, Indian sarees, salwar kameez and more. Pre-loved, always loved.', img: 'https://images.unsplash.com/photo-1583744026327-f8b2a4e3e462?w=1920&h=600&fit=crop&auto=format&q=80', count: '312' },
  accessories: { title: 'Accessories',        intro: 'Scarves, belts, jewellery, sunglasses, hats and wallets. Every piece Stripe-secured and authenticity-checked.', img: 'https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=1920&h=600&fit=crop&auto=format&q=80', count: '428' },
  fragrance:   { title: 'Fragrance',          intro: 'Half-used, sealed, or rare - discover scents at sensible prices.',                                           img: 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=1920&h=600&fit=crop&auto=format&q=80', count: '187' },
};

const subcategories: Record<string, { label: string; count: number }[]> = {
  womenswear:  [{label:'Coats & Jackets',count:218},{label:'Knitwear',count:194},{label:'Jeans',count:156},{label:'Dresses',count:142},{label:'Tops & Shirts',count:128},{label:'Skirts & Trousers',count:98},{label:'Suits & Blazers',count:87},{label:'Activewear',count:61},{label:'Loungewear',count:52},{label:'Vintage',count:148}],
  menswear:    [{label:'Coats & Jackets',count:142},{label:'Knitwear',count:118},{label:'Jeans',count:128},{label:'Shirts',count:96},{label:'Suits',count:74},{label:'T-shirts',count:212},{label:'Trousers',count:88},{label:'Activewear',count:54},{label:'Vintage',count:120}],
  electronics: [{label:'Phones',count:148},{label:'Laptops',count:62},{label:'Tablets',count:38},{label:'Headphones',count:35},{label:'Cameras',count:22}],
  traditional: [{label:'Ankara & Kente',count:87},{label:'Adire & Tie-Dye',count:63},{label:'Sarees & Lehengas',count:72},{label:'Salwar Kameez',count:58},{label:'Kaftans',count:44},{label:'Aso-Oke',count:31},{label:'Embroidered Sets',count:49},{label:'Fabric Lengths',count:28}],
  accessories: [{label:'Scarves',count:94},{label:'Belts',count:68},{label:'Jewellery',count:112},{label:'Sunglasses',count:74},{label:'Hats & Caps',count:55},{label:'Wallets',count:82},{label:'Hair Accessories',count:63},{label:'Clutches',count:49}],
  default:     [{label:'New in',count:48},{label:'Trending',count:36},{label:'Under £50',count:182},{label:'Verified premium',count:94},{label:'Open to offers',count:218}],
};

const topBrands: Record<string, string[]> = {
  womenswear:  ['Acne Studios', 'Burberry', 'COS', 'Ganni', 'Isabel Marant', 'Loewe', 'Max Mara', 'The Row', 'Toteme', 'Zara'],
  menswear:    ['Acne Studios', 'Carhartt', 'COS', 'Levi\'s', 'Stüssy', 'Paul Smith', 'Patagonia', 'Stone Island'],
  electronics: ['Apple', 'Samsung', 'Sony', 'Bose', 'Dyson', 'Sonos', 'Google'],
  traditional: ['Vlisco', 'House of Deola', 'Ozwald Boateng', 'Lisa Folawiyo', 'Duro Olowu', 'Sabyasachi', 'Anita Dongre', 'Manish Malhotra', 'Tarun Tahiliani', 'Payal Singhal'],
  accessories: ['Hermès', 'Gucci', 'Bottega Veneta', 'Cartier', 'Loewe', 'Acne Studios', 'Mulberry', 'Missoma', 'Toteme', 'Prada'],
  default:     ['Acne Studios', 'Apple', 'Burberry', 'COS', 'Loewe', 'Max Mara', 'Mulberry', 'Nike', 'Sony'],
};

const colourSwatches = [
  { v: 'black',  hex: '#0F0F12' },
  { v: 'cream',  hex: '#F2EDE3', border: true },
  { v: 'camel',  hex: '#94815a' },
  { v: 'forest', hex: '#3F5A4D' },
  { v: 'rouge',  hex: '#B23A48' },
  { v: 'navy',   hex: '#1D2735' },
  { v: 'plum',   hex: '#5A3A52' },
  { v: 'gold',   hex: '#D98A2B' },
  { v: 'grey',   hex: '#9A9AA3' },
  { v: 'clay',   hex: '#C2614A' },
  { v: 'white',  hex: '#FAF7F2', border: true },
];

const conditionLabels = [
  { v: 'NEW_TAGS',  label: 'New with tags', dots: 5 },
  { v: 'LIKE_NEW',  label: 'Like new',      dots: 5 },
  { v: 'EXCELLENT', label: 'Excellent',     dots: 4 },
  { v: 'GOOD',      label: 'Good',          dots: 3 },
  { v: 'FAIR',      label: 'Fair',          dots: 2 },
];

const types = [
  { v: '',      label: 'All listings' },
  { v: 'SALE',  label: 'Buy now' },
  { v: 'OFFER', label: 'Offers open' },
  { v: 'SWAP',  label: 'Swap-able' },
];

const histogram = [30, 55, 80, 95, 70, 50, 35, 25, 18, 12]; // visual density of price band


/* ────────────── Component ────────────── */

export default function Category() {
  const { slug = 'all' } = useParams();

  // Which slugs are real: the static category pages (headerMeta), plus a few
  // virtual collections that map onto real queries rather than a DB category.
  // Anything else is a genuine not-found - we never silently fall back to
  // "All listings".
  const VIRTUAL: Record<string, { title: string; intro: string }> = {
    new:   { title: 'New in',      intro: 'The latest arrivals across Souqli, verified and payment-protected, freshest first.' },
    flash: { title: 'Flash drops', intro: 'Limited-time price drops from Souqli sellers. Once they are gone, they are gone.' },
    drops: { title: 'Flash drops', intro: 'Limited-time price drops from Souqli sellers. Once they are gone, they are gone.' },
  };
  const isVirtual = slug in VIRTUAL;
  const isKnown = slug === 'all' || slug in headerMeta || isVirtual;
  const flashOnly = slug === 'flash' || slug === 'drops';
  // Virtual collections draw from the whole catalogue, then filter client-side.
  const apiCategory = isVirtual ? 'all' : slug;

  const meta = headerMeta[slug]
    ?? (isVirtual ? { ...headerMeta.all, title: VIRTUAL[slug].title, intro: VIRTUAL[slug].intro } : headerMeta.all);
  const subs = subcategories[slug] ?? subcategories.default;
  const brands = topBrands[slug] ?? topBrands.default;

  // Recently viewed - ids come from the visitor's own view history (this
  // browser), then the live listing data (real photo, title, price) is fetched
  // so the strip shows the actual products. Sold / removed items drop out.
  const recentIds = useMemo(() => getHistory().slice(0, 12).map((r) => r.id), []);
  const { data: recentLive = [] } = useQuery<Product[]>({
    queryKey: ['recently-viewed', recentIds.join(',')],
    enabled: recentIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => (await api.get('/products/recent', { params: { ids: recentIds.join(',') } })).data,
  });
  const recentTiles = recentLive.slice(0, 8);

  const [searchParams] = useSearchParams();

  // Filters start neutral so a category shows everything; the user (or a nav
  // link's query params) then narrows the grid.
  const [activeSub,  setActiveSub]  = useState<string>('');
  const [sort,       setSort]       = useState('newest');
  const [condition,  setCondition]  = useState<string>('any');
  const [type,       setType]       = useState<string>('');
  const [maxPrice,   setMaxPrice]   = useState(0);
  const [minPrice,   setMinPrice]   = useState(0);
  const [view,       setView]       = useState<'grid'|'mosaic'>('grid');
  const [visibleCount, setVisibleCount] = useState(36);
  const [selectedSizes,  setSelectedSizes]  = useState<Set<string>>(new Set());
  const [selectedColors, setSelectedColors] = useState<Set<string>>(new Set());
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set());
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [offersOpen,   setOffersOpen]   = useState(false);
  const [lockerOnly,   setLockerOnly]   = useState(false);
  const [brandQuery,   setBrandQuery]   = useState('');
  const [filtersOpen,  setFiltersOpen]  = useState(false);
  // How many refinements are applied (badge on the Filter & sort button).
  const refineCount =
    (sort !== 'newest' ? 1 : 0) + (activeSub ? 1 : 0) + (type ? 1 : 0) + (condition !== 'any' ? 1 : 0) +
    (minPrice ? 1 : 0) + (maxPrice ? 1 : 0) +
    selectedSizes.size + selectedColors.size + selectedBrands.size +
    (verifiedOnly ? 1 : 0) + (offersOpen ? 1 : 0) + (lockerOnly ? 1 : 0);
  const [query,        setQuery]        = useState('');

  // Sync filters from the URL whenever the route or query string changes
  // (e.g. clicking "Men > Shirts" -> /c/menswear?sub=Shirts, or ?brand=, ?condition=).
  useEffect(() => {
    setActiveSub(searchParams.get('sub') ?? '');
    const brand = searchParams.get('brand');
    setSelectedBrands(brand ? new Set([brand]) : new Set());
    setCondition(searchParams.get('condition') ?? 'any');
    const srt = searchParams.get('sort');
    setSort(srt === 'new' ? 'newest' : (srt ?? 'newest'));
    const max = searchParams.get('max');
    setMaxPrice(max ? Number(max) : 0);
    setType(searchParams.get('type') ?? '');
    setQuery(searchParams.get('q') ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, slug]);

  const { data: products = [], isLoading } = useQuery<Product[]>({
    queryKey: ['category', slug, sort, condition, type, maxPrice],
    queryFn: async () => (await api.get('/products', {
      params: { category: apiCategory, sort, condition, type: type || undefined, maxPrice: maxPrice || undefined },
    })).data,
    enabled: isKnown,
  });

  // Base list: backend results, or category-filtered demo data as a fallback.
  const base = useMemo<Product[]>(() => {
    const src = products.length ? products : (SHOW_DEMO ? demoByCategory(apiCategory) : []);
    return flashOnly ? src.filter((p) => (p as { flash?: boolean }).flash) : src;
  }, [products, apiCategory, flashOnly]);

  // Category-relevant "quiet luxuries": the three highest-value pieces in this
  // category. Only shown on a real category page, never on the all/search view.
  const luxuryPicks = useMemo(() => {
    if (slug === 'all') return [];
    return [...base].filter(Boolean)
      .sort((a, b) => (b.priceGBP || 0) - (a.priceGBP || 0))
      .slice(0, 3)
      .map((p) => ({
        id: p.id,
        brand: p.brand ?? 'Souqli',
        title: p.title,
        price: p.priceGBP,
        condition: `${conditionLabels.find((c) => c.v === p.condition)?.label ?? 'Verified'}${p.size ? ` · ${p.size}` : ''}`,
        img: p.imageUrl ?? galleryForGarment('other', '', seedFromId(p.id), 1)[0],
      }));
  }, [base, slug]);

  // Apply every active facet, then sort. This is what makes the nav links and
  // the sidebar actually narrow the grid.
  const items = useMemo<Product[]>(() => {
    let list = base.filter(Boolean);
    if (query) {
      const q = query.toLowerCase();
      // Search title, brand, code, size AND the item's derived colour, so "black"
      // or "navy" returns products whose colour matches after checking details.
      list = list.filter((p) =>
        `${p.title ?? ''} ${p.brand ?? ''} ${(p as { code?: string }).code ?? ''} ${p.size ?? ''} ${productColour(p) ?? ''}`.toLowerCase().includes(q),
      );
    }
    if (activeSub) list = list.filter((p) => matchesSubcategory(p, activeSub));
    if (selectedBrands.size) list = list.filter((p) => !!p.brand && selectedBrands.has(p.brand));
    if (condition !== 'any') list = list.filter((p) => p.condition === condition);
    if (type) list = list.filter((p) => (p.type ?? 'SALE') === type);
    if (selectedSizes.size) {
      list = list.filter((p) => p.size && [...selectedSizes].some((s) => p.size!.toUpperCase().includes(s.toUpperCase())));
    }
    if (selectedColors.size) list = list.filter((p) => [...selectedColors].some((c) => matchesColour(p, c)));
    if (minPrice) list = list.filter((p) => p.priceGBP >= minPrice);
    if (maxPrice) list = list.filter((p) => p.priceGBP <= maxPrice);
    if (verifiedOnly) list = list.filter((p) => p.verified);
    if (offersOpen) list = list.filter((p) => p.type === 'OFFER' || (p as { acceptsOffers?: boolean }).acceptsOffers);
    // lockerOnly: every Souqli item ships via locker -> no-op filter.

    const sorted = [...list];
    if (sort === 'price_asc') sorted.sort((a, b) => a.priceGBP - b.priceGBP);
    else if (sort === 'price_desc') sorted.sort((a, b) => b.priceGBP - a.priceGBP);
    else if (sort === 'trending') sorted.sort((a, b) => (b.saveCount ?? 0) - (a.saveCount ?? 0));
    return sorted;
  }, [base, query, activeSub, selectedBrands, condition, type, selectedSizes, selectedColors, minPrice, maxPrice, verifiedOnly, offersOpen, sort]);

  // A new filter result starts the "load more" count over.
  useEffect(() => { setVisibleCount(36); }, [items]);

  const toggle = <T,>(set: Set<T>, value: T, setter: (s: Set<T>) => void) => {
    const next = new Set(set);
    next.has(value) ? next.delete(value) : next.add(value);
    setter(next);
  };

  const filteredBrands = brandQuery
    ? brands.filter((b) => b.toLowerCase().includes(brandQuery.toLowerCase()))
    : brands;

  if (!isKnown) {
    return (
      <div className="bg-cream">
        <Seo title="Collection not found" noindex />
        <div className="max-w-[640px] mx-auto px-6 py-24 text-center">
          <div className="text-5xl mb-4" aria-hidden>🧭</div>
          <h1 className="display text-3xl">Collection not found</h1>
          <p className="text-mute mt-3 leading-relaxed max-w-[46ch] mx-auto">
            We couldn't find a "{slug}" collection. It may have been renamed, or isn't available yet.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to="/c/all" className="btn btn-primary">Browse all listings</Link>
            <Link to="/" className="btn btn-outline">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream">
      <Seo title={meta.title} description={meta.intro} />
      {/* ─── Editorial header with hero photograph ─── */}
      <header className="section-dark relative bg-ink text-cream overflow-hidden">
        <img
          src={meta.img}
          alt=""
          aria-hidden
          onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&h=600&fit=crop&auto=format&q=80'; }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(15,15,18,.92), rgba(15,15,18,.7) 50%, rgba(15,15,18,.3))' }} />
        <div className="absolute inset-0 bg-gold-mesh opacity-50" />
        <div className="absolute inset-0 dotgrid-light bg-[length:24px_24px] opacity-30" />
        <div className="relative max-w-[1800px] mx-auto px-6 py-14 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <nav className="stamp text-mute-soft mb-3 flex items-center gap-2">
              <Link to="/" className="hover:text-cream">HOME</Link>
              <span>/</span>
              <Link to="/c/all" className="hover:text-cream">SHOP</Link>
              <span>/</span>
              <span className="text-cream uppercase">{meta.title}</span>
            </nav>
            <div className="stamp text-gold mb-2">Category</div>
            <h1 className="display text-5xl md:text-6xl">{meta.title}</h1>
            <p className="mt-3 text-mute-soft max-w-[60ch] text-base leading-relaxed">{meta.intro}</p>
            <div className="mt-5 flex items-center gap-3 text-sm text-mute-soft">
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-gold" />{isLoading ? 'Loading…' : `${items.length} ${items.length === 1 ? 'piece' : 'pieces'}`}</span>
              <span>·</span><span>Updated daily</span>
              <span>·</span><span className="text-gold">All payment-protected</span>
            </div>
          </div>
          {/* Editor's note glass card */}
          <div className="lg:col-span-5 lg:flex lg:justify-end">
            <div className="card-glass p-4 max-w-[340px]" style={{ background: 'rgba(250,247,242,.92)' }}>
              <div className="stamp text-gold-dark text-[9px]">EDITOR&rsquo;S NOTE</div>
              <p className="text-[13px] text-ink mt-2 leading-relaxed italic">
                &ldquo;This week - heritage outerwear is back. Our team has hand-picked the cleanest cashmere and best-cut tailoring for the season.&rdquo;
              </p>
              <div className="mt-3 flex items-center gap-2 text-[11px] text-mute">
                <span className="w-6 h-6 rounded-full bg-gradient-to-br from-gold to-gold-dark" />
                <span>Léa, Souqli editor</span>
              </div>
            </div>
          </div>
        </div>
        <div className="gold-rule-thick" />
      </header>

      {/* ─── Sub-category chip rail ─── */}
      <div className="bg-cream-deep border-b border-warmline">
        <div className="max-w-[1800px] mx-auto px-6 py-4 flex items-center gap-3 overflow-x-auto no-scrollbar">
          <span className="stamp text-mute text-[9px] mr-2 shrink-0">SHOP</span>
          <button onClick={() => setActiveSub('')} className={`px-3.5 py-1.5 text-xs rounded-full font-medium shrink-0 transition ${activeSub === '' ? 'bg-ink text-cream' : 'bg-cream text-ink hover:bg-ink hover:text-cream'}`}>
            All <span className="opacity-60 ml-1">{base.filter(Boolean).length}</span>
          </button>
          {subs.map((s) => {
            const n = base.filter((p) => matchesSubcategory(p, s.label)).length;
            return (
            <button
              key={s.label}
              onClick={() => setActiveSub(activeSub === s.label ? '' : s.label)}
              className={`px-3.5 py-1.5 text-xs rounded-full shrink-0 transition ${activeSub === s.label ? 'bg-ink text-cream font-medium' : 'bg-cream text-ink hover:bg-ink hover:text-cream'}`}
            >
              {s.label}{n > 0 && <span className="text-mute ml-1">{n}</span>}
            </button>
            );
          })}
        </div>
      </div>

      {/* ─── Editor's Pick band - only on a real category; items drawn from it ─── */}
      {slug !== 'all' && luxuryPicks.length > 0 && (
      <section className="max-w-[1800px] mx-auto px-6 pt-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="stamp text-gold-dark text-[9px]">EDITOR&rsquo;S PICK · WEEK 19</div>
            <h2 className="display text-2xl mt-1.5">Three quiet luxuries in {meta.title}</h2>
          </div>
          <Link to={`/c/${slug}`} className="stamp text-gold-dark hover:underline text-[11px]">View all →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {luxuryPicks.map((p, i) => (
            <Link key={p.id} to={`/p/${p.id}`} className="card-flat p-3 flex gap-3 hover:shadow-luxe hover:-translate-y-0.5 transition group">
              <img
                src={p.img}
                alt={`${p.brand} ${p.title}`}
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).src = galleryForGarment('other', '', seedFromId(p.id), 1)[0]; }}
                className="w-20 h-24 notched-sm object-cover shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="stamp text-mute text-[9px]">{p.brand.toUpperCase()}</div>
                <div className="text-sm mt-0.5 truncate group-hover:text-gold-dark transition">{p.title}</div>
                <div className="display text-base mt-1">£{p.price}</div>
                <div className="flex items-center gap-1 mt-1 text-[10px] text-mute"><span className="w-1.5 h-1.5 rounded-full bg-ok" /> {p.condition}</div>
              </div>
              <span className="stamp text-gold-dark text-[9px] self-start mt-1">PICK · 0{i + 1}</span>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* ─── Sticky filter toolbar with active chips ─── */}
      <div className="sticky top-[72px] z-30 bg-cream/95 backdrop-blur border-y border-warmline mt-8">
        <div className="max-w-[1800px] mx-auto px-6 py-3 flex flex-wrap items-center gap-3">
          <div className="order-first w-full sm:order-none sm:w-auto flex items-center gap-2">
            <div className="relative flex-1 min-w-0 sm:w-60 sm:flex-none">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mute pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.2-3.2"/></svg>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search items or brands…"
                className="input w-full pl-9 pr-3 py-1.5 text-[13px]"
                aria-label="Search listings"
              />
            </div>
            <button
              onClick={() => setFiltersOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-4 h-[38px] text-[12px] font-medium hover:bg-gold hover:text-ink transition shrink-0"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M3 6h18M6 12h12M10 18h4" />
              </svg>
              Filter &amp; sort
              {refineCount > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-ink text-[10px] font-bold grid place-items-center">{refineCount}</span>
              )}
            </button>
          </div>
          <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-3">
            <span className="text-[12px] text-mute"><span className="text-ink font-medium">{items.length}</span> matches</span>
            <span className="hidden sm:inline h-5 w-px bg-warmline" />
            <div className="flex bg-cream-deep rounded-full p-1">
              <button onClick={() => setView('grid')}   className={`px-3 py-1 text-[11px] rounded-full ${view === 'grid' ? 'bg-ink text-cream' : 'text-mute'}`}>▦ Grid</button>
              <button onClick={() => setView('mosaic')} className={`px-3 py-1 text-[11px] rounded-full ${view === 'mosaic' ? 'bg-ink text-cream' : 'text-mute'}`}>▩ Mosaic</button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Filter drawer + full-width product grid ─── */}
      <div className="max-w-[1800px] mx-auto px-6 py-8">

        {/* ── FILTER DRAWER ── */}
        {filtersOpen && (
          <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={() => setFiltersOpen(false)} />
            <aside className="absolute inset-x-0 bottom-0 max-h-[88vh] rounded-t-3xl pb-[env(safe-area-inset-bottom)] bg-cream shadow-frame flex flex-col animate-in slide-in-from-bottom duration-200 sm:inset-x-auto sm:inset-y-0 sm:left-0 sm:bottom-auto sm:max-h-none sm:rounded-t-none sm:pb-0 sm:w-[380px] sm:slide-in-from-left">
              <div className="sm:hidden mx-auto mt-2.5 mb-0.5 h-1.5 w-10 rounded-full bg-warmline shrink-0" aria-hidden />
              <div className="flex items-center justify-between px-5 py-4 border-b border-warmline shrink-0">
                <div>
                  <div className="stamp text-gold-dark text-[9px]">REFINE</div>
                  <div className="display text-xl leading-none mt-1">Filters</div>
                </div>
                <button onClick={() => setFiltersOpen(false)} aria-label="Close filters"
                  className="w-9 h-9 rounded-full grid place-items-center text-mute hover:bg-cream-deep">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Sort - lives inside the drawer now */}
          <div className="card-flat p-4">
            <div className="stamp text-gold-dark text-[9px] mb-3">SORT BY</div>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Sort listings">
              {[
                { v: 'newest',     label: 'Newest first' },
                { v: 'price_asc',  label: 'Price: low to high' },
                { v: 'price_desc', label: 'Price: high to low' },
                { v: 'trending',   label: 'Most loved' },
              ].map((o) => (
                <button
                  key={o.v}
                  role="radio"
                  aria-checked={sort === o.v}
                  onClick={() => setSort(o.v)}
                  className={`min-h-[40px] px-3 py-2 text-[12px] rounded-xl text-left transition ${sort === o.v ? 'bg-ink text-cream font-medium' : 'bg-cream-deep text-ink hover:bg-ink/10'}`}
                >{sort === o.v ? '✓ ' : ''}{o.label}</button>
              ))}
            </div>
          </div>

          {/* Type pills (mobile-friendly above the brand list) */}
          <div className="card-flat p-3">
            <div className="flex flex-wrap gap-1.5">
              {types.map((t) => (
                <button
                  key={t.label}
                  onClick={() => setType(t.v)}
                  className={`px-3 py-1.5 text-[11px] rounded-full transition ${type === t.v ? 'bg-ink text-cream' : 'text-mute hover:text-ink bg-cream-deep'}`}
                >{t.label}</button>
              ))}
            </div>
          </div>

          {/* Brand */}
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="stamp text-gold-dark text-[9px]">BRAND</div>
              <span className="text-[10px] text-mute">{selectedBrands.size} selected</span>
            </div>
            <input
              value={brandQuery}
              onChange={(e) => setBrandQuery(e.target.value)}
              placeholder="Search brands…"
              className="input text-[12px] py-1.5 mb-3"
            />
            <ul className="space-y-2 text-[12px] max-h-[180px] overflow-y-auto">
              {filteredBrands.map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="accent-gold"
                    checked={selectedBrands.has(b)}
                    onChange={() => toggle(selectedBrands, b, setSelectedBrands)}
                  />
                  <span className="flex-1">{b}</span>
                  <span className="text-mute">{Math.floor(Math.random() * 80 + 4)}</span>
                </li>
              ))}
            </ul>
            <button className="text-[11px] text-gold-dark mt-2 hover:underline">+ Show all 184 brands</button>
          </div>

          {/* Size */}
          <div className="card-flat p-4">
            <div className="stamp text-gold-dark text-[9px] mb-3">SIZE</div>
            <div className="grid grid-cols-4 gap-1.5">
              {['XS','S','M','L','XL','XXL','2XS','+'].map((s) => (
                <button
                  key={s}
                  onClick={() => s !== '+' && toggle(selectedSizes, s, setSelectedSizes)}
                  className={`aspect-square text-[11px] rounded transition ${selectedSizes.has(s) ? 'border-2 border-ink bg-ink text-cream' : 'border border-warmline text-ink hover:border-ink'} ${s === '+' ? 'text-mute' : ''}`}
                >{s}</button>
              ))}
            </div>
          </div>

          {/* Colour */}
          <div className="card-flat p-4">
            <div className="stamp text-gold-dark text-[9px] mb-3">COLOUR</div>
            <div className="grid grid-cols-6 gap-2">
              {colourSwatches.map((c) => (
                <button
                  key={c.v}
                  onClick={() => toggle(selectedColors, c.v, setSelectedColors)}
                  title={c.v}
                  className={`aspect-square rounded-full transition ${c.border ? 'border border-warmline' : ''} ${selectedColors.has(c.v) ? 'ring-2 ring-gold ring-offset-2 ring-offset-cream' : ''}`}
                  style={{ background: c.hex }}
                />
              ))}
              <button className="aspect-square rounded-full border-2 border-dashed border-warmline text-[8px] text-mute">+</button>
            </div>
          </div>

          {/* Price histogram + range */}
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="stamp text-gold-dark text-[9px]">PRICE</div>
              <span className="text-[10px] text-mute">£{minPrice} - £{maxPrice || 980}</span>
            </div>
            <div className="flex items-end gap-0.5 h-12 mb-2">
              {histogram.map((h, i) => (
                <div key={i} className="flex-1 bg-gold/40 rounded-t" style={{ height: `${h}%`, opacity: 0.3 + h / 200 }} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input className="input text-[11px] py-1" type="number" min={0} placeholder="£min" value={minPrice || ''} onChange={(e) => setMinPrice(Number(e.target.value))} />
              <input className="input text-[11px] py-1" type="number" min={0} placeholder="£max" value={maxPrice || ''} onChange={(e) => setMaxPrice(Number(e.target.value))} />
            </div>
          </div>

          {/* Condition */}
          <div className="card-flat p-4">
            <div className="stamp text-gold-dark text-[9px] mb-3">CONDITION</div>
            <ul className="space-y-2 text-[12px]">
              <li className="flex items-center gap-2">
                <input type="radio" name="cond" className="accent-gold" checked={condition === 'any'} onChange={() => setCondition('any')} />
                <span className="flex-1">Any</span>
                <span className="text-mute">{items.length}</span>
              </li>
              {conditionLabels.map((c) => (
                <li key={c.v} className="flex items-center gap-2">
                  <input type="radio" name="cond" className="accent-gold" checked={condition === c.v} onChange={() => setCondition(c.v)} />
                  <span className="flex-1 flex items-center gap-1.5">
                    <span className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i} className={`w-1 h-1 rounded-full ${i < c.dots ? 'bg-gold' : 'bg-warmline'}`} />
                      ))}
                    </span>
                    {c.label}
                  </span>
                  <span className="text-mute">{Math.floor(Math.random() * 80 + 6)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Toggles */}
          <div className="card-flat p-4 space-y-3">
            <div className="stamp text-gold-dark text-[9px]">FILTERS</div>
            <Toggle label="Verified only"           value={verifiedOnly} onChange={setVerifiedOnly} />
            <Toggle label="Open to offers"          value={offersOpen}   onChange={setOffersOpen} />
            <Toggle label="Locker delivery only"    value={lockerOnly}   onChange={setLockerOnly} />
          </div>

                <button className="w-full btn btn-outline btn-sm">🔔 Save this search</button>
              </div>

              <div className="px-5 py-4 border-t border-warmline flex gap-2 shrink-0">
                <button
                  onClick={() => { setSort('newest'); setActiveSub(''); setType(''); setCondition('any'); setMinPrice(0); setMaxPrice(0); setSelectedSizes(new Set()); setSelectedColors(new Set()); setSelectedBrands(new Set()); setVerifiedOnly(false); setOffersOpen(false); setLockerOnly(false); }}
                  className="btn btn-outline btn-sm flex-1"
                >
                  Clear all
                </button>
                <button onClick={() => setFiltersOpen(false)} className="btn btn-primary btn-sm flex-[2]">
                  Show {items.length} result{items.length === 1 ? '' : 's'}
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* ── PRODUCT GRID (full width) ── */}
        <main>
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i}>
                  <div className="aspect-[4/5] notched-sm skeleton" />
                  <div className="mt-3 h-3 w-1/3 skeleton rounded" />
                  <div className="mt-2 h-4 w-3/4 skeleton rounded" />
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center">
              <div className="display text-3xl text-mute">No matches yet</div>
              <p className="text-sm text-mute mt-2">Try widening the filters or check back tomorrow - fresh drops daily.</p>
              <Link to="/c/all" className="btn btn-outline mt-6">Browse all categories</Link>
            </div>
          ) : view === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-x-4 gap-y-10">
              {items.slice(0, visibleCount).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="columns-2 md:columns-4 xl:columns-5 gap-5 [column-fill:_balance]">
              {items.slice(0, visibleCount).map((p, i) => (
                <div key={p.id} className={`mb-12 break-inside-avoid ${i % 5 === 0 ? 'mt-6' : ''}`}>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          )}

          {/* Pagination - real client-side "load more" */}
          {items.length > 0 && (
            <div className="mt-10 flex flex-col items-center gap-4">
              <div className="text-[12px] text-mute">Showing <span className="text-ink font-medium">{Math.min(visibleCount, items.length)} of {items.length}</span> matches</div>
              <div className="w-64 h-1 bg-cream-deep rounded-full overflow-hidden">
                <div className="h-full bg-gold rounded-full" style={{ width: `${Math.min(100, (Math.min(visibleCount, items.length) / items.length) * 100)}%` }} />
              </div>
              {visibleCount < items.length && (
                <button className="btn btn-outline" onClick={() => setVisibleCount((v) => v + 36)}>
                  Load {Math.min(36, items.length - visibleCount)} more →
                </button>
              )}
            </div>
          )}

          {/* Recently viewed - the visitor's real recently viewed listings,
              live from the catalogue. Hidden until there is history. */}
          {recentTiles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-warmline">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="stamp text-gold-dark text-[9px]">RECENTLY VIEWED</div>
                  <h3 className="display text-xl mt-1">Pieces you&rsquo;ve been eyeing</h3>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
                {recentTiles.map((t) => (
                  <Link key={t.id} to={`/p/${t.id}`} className="group block min-w-0">
                    <div className="relative aspect-square notched-sm overflow-hidden bg-cream-deep">
                      {t.imageUrl ? (
                        <img
                          src={t.imageUrl}
                          alt={[t.brand, t.title].filter(Boolean).join(' ')}
                          loading="lazy"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                          className="w-full h-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-mute text-[11px]">No photo</div>
                      )}
                    </div>
                    <div className="mt-2 px-0.5">
                      {t.brand && <div className="stamp text-mute text-[9px] truncate">{t.brand.toUpperCase()}</div>}
                      <div className="text-[13px] leading-snug truncate group-hover:text-gold-dark transition">{t.title}</div>
                      <div className="display text-base mt-0.5">£{Number(t.priceGBP).toFixed(2).replace(/\.00$/, '')}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-2 text-[12px] cursor-pointer">
      <span>{value ? '✓ ' : ''}{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative inline-block w-8 h-4 rounded-full transition ${value ? 'bg-gold' : 'bg-cream-deep'}`}
      >
        <span className={`absolute top-0 w-4 h-4 rounded-full bg-cream shadow transition-all ${value ? 'right-0' : 'left-0 border border-warmline'}`} />
      </button>
    </label>
  );
}
